import React, { useEffect, useState, useMemo, memo } from "react";
import { MainContainer, ChatContainer, MessageList, Message, MessageInput, ConversationHeader, Sidebar, Search, ConversationList, Conversation } from '@chatscope/chat-ui-kit-react';
import "../../styles/index.css";
import { getFromHub, postToHub, putToHub } from "../../network/actions";
import NetworkWrapper from "../NetworkWrapper";
import { Chip } from 'primereact/chip';
import { Dialog } from "primereact/dialog";
import { InputTextarea } from "primereact/inputtextarea";
import { InputSwitch } from "primereact/inputswitch";
import { Dropdown } from "primereact/dropdown";
import { Button } from "primereact/button";
import { Divider } from "primereact/divider";
import { useNotificationContext } from "../../context/NotificationContext";
import { InputText } from "primereact/inputtext";
import { MultiSelect } from "primereact/multiselect";
import { Badge } from "primereact/badge";
import useSSE from "../../hooks/useSSE";
import { truncateText } from "../cmsbase/helpers";
import { PHOENIX_NODES, isConciergeAllowed } from "../../network/constants";

const Concierge = () => {

    const [selectedNode, setSelectedNode] = useState(() => {
        try {
          const stored = localStorage.getItem("shipNode");
          if (!stored) return null;
      
          const { id } = JSON.parse(stored);
      
          return PHOENIX_NODES.find(node => node.id === id) ?? null;
        } catch {
          return null;
        }
      });
      

    const getOptions = () => {
        const shipOptions = PHOENIX_NODES.map(node => ({name: node.name, code: node.code}));
        return [
            {name: 'Select a Ship', code: "", disabled: true},
            ...shipOptions
        ]
    }

    const handleShipNodeChange = (e) => {
        const newNode = PHOENIX_NODES.find(node => node.code === e.value);
        if(newNode){
            setSelectedNode(newNode);
            localStorage.setItem("shipNode", JSON.stringify(newNode));
        }else{
            setSelectedNode(null);
            localStorage.removeItem("shipNode");
        }
    }

    if(!isConciergeAllowed()){
        return null;
    }

    return (
        <div>
            <div className="pb-4 grid grid-cols-2 md:grid-cols-3">
                <div>
                    <label htmlFor="ship">Change Ship: </label>
                    <div className="mt-1">
                        <Dropdown
                            id="ship"
                            name="ship"
                            value={selectedNode?.code || ""}
                            onChange={handleShipNodeChange}
                            options={getOptions()}
                            optionLabel="name"
                            optionValue="code"
                            className="w-9/12 max-w-9/12"
                        />
                    </div>
                </div>
            </div>
            {selectedNode && <ConciergePanel nodeId={selectedNode.id} />}
        </div>
        
    )
}

export default Concierge;

const ConciergePanel = memo(function ConciergePanel(nodeId){
    
    const [allStates, setAllStates] = useState([]);
    const [allPriorities, setAllPriorities] = useState([]);
    const [allProblems, setAllProblems] = useState([]);
    const [selectedProblem, setSelectedProblem] = useState(null);
    const [showDialog, setShowDialog] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [loadingMessages, setLoadingMessages] = useState(false);
    const [chat, setChat] = useState(null);
    const [message, setMessage] = useState("");

    const {toast} = useNotificationContext();
    
    useSSE({
        chatMessageCallback: function (event) {
            if(!event.problemId){
                return;
            }
    
            // Append the message when the conversation is open; otherwise, add a marker to indicate a new message
            if((event.problemId === selectedProblem.id) && chat && event.message){
                setChat(prevChat => ({
                    ...prevChat,
                    messages: [
                        ...prevChat.messages,
                        {
                            chat_id: event.chat_id,
                            sender_id: event.sender_id,
                            senderName: event.senderName,
                            message: event.message
                        }
                    ]
                }));
            } else {
                setAllProblems(prevProblems => {
                    return prevProblems.map(prevProblem => {
                        if (prevProblem.id === event.problemId) {
                            return { ...prevProblem, hasUnreadMessages: true };
                        }
                        return prevProblem;
                    });
                });
            }
        },
        nodeId
    });

    // Layout control helpers
    const isMobileView = window.innerWidth > 768 ? false : true; //tailwind medium screen break point (768px)
    const showOnlyListOnMobile = isMobileView && !selectedProblem;
    const showListInMainLayout = !isMobileView || !selectedProblem;

    const fetchAllStates = async () => {
        await getFromHub("problem/v2/admin/states", (data) => {
            const states = data.map(state => ({label: state.name, value: state.name}))
            setAllStates(states);
        });
    };

    const fetchAllPriorities = async () => {
        await getFromHub("problem/v2/admin/priorities", (data) => {
            const priorities = data.map(priority => ({label: priority.name, value: priority.name}))
            setAllPriorities(priorities);
        });
    };

    const fetchAllProblems = async () => {
        await getFromHub(
            "problem/v2/admin/problems", 
            (data) => { setAllProblems(data) }, // success
            () => { setAllProblems([]) } // failure
        );
    }

    useEffect(() => {
        const fetchProblems = async () => {
            setIsLoading(true);
            try {
                await fetchAllProblems();
            } finally {
                setIsLoading(false);
            }
        };
        fetchProblems();

        // Reset data every time nodeId changed
        setSelectedProblem(null);
        setChat(null);
        setMessage("");
        
    }, [nodeId]);

    useEffect(()=>{
        const fetchData = async () => {
            await fetchAllStates();
            await fetchAllPriorities();
        };

        fetchData();
    },[])


    const handleConversationClick = (problem) => {
        if(selectedProblem?.id === problem.id){
            return;
        }

        setSelectedProblem(problem);
        setLoadingMessages(true);
        setMessage("");
        // Get Messages
        postToHub(
            `problem/v2/admin/chat/${problem.id}/init`,
            null,
            (data) => { // success
                setChat(data.chat);
                setLoadingMessages(false);

                //Remove dot marker if existed
                setAllProblems(prevProblems => {
                    return prevProblems.map(prevProblem => {
                        if (prevProblem.id === problem.id) {
                            return { ...prevProblem, hasUnreadMessages: false };
                        }
                        return prevProblem;
                    });
                });
            },
            () => { //Failure
                setChat(null);
                toast.current.show({severity: 'error', summary: 'Oops! unable to get the conversation.'});
                setLoadingMessages(false);
            }
        )
    }

    const sendMessage = (data) => {
        setMessage("");
        let payload = {message: data};
        postToHub(
            `problem/v2/admin/chat/${selectedProblem.id}`,
            payload,
            (message)=>{ // success
                if(chat && message){
                    setChat(prevChat => ({
                        ...prevChat,
                        messages: [...prevChat.messages, message]
                    }));
                }
            }, 
            ()=>{ // failure
                toast.current.show({severity: 'error', summary: 'Uh oh! Failed to Send Message'});
                setMessage(data);
            }
        )
    }

    const updateProblemInState = (updatedProblem) => {
        setSelectedProblem(updatedProblem);
        const updatedProblems = allProblems.map(problem => {
            if(problem.id === updatedProblem.id){
                return updatedProblem
            }
            return problem
        })
        setAllProblems(updatedProblems);
    }

    const getStatusIcon = (status) => {
        switch (status) {
            case "Received":
                return "pi pi-arrow-down-right";
            case "InProgress":
                return "pi pi-wrench";
            case "InvestigationRequired":
                return "pi pi-globe";
            case "Resolved":
                return "pi pi-check";
            case "Unresolved":
                return "pi pi-times-circle";
            default:
                return "pi pi-refresh";
        }
    }

    return (
        <NetworkWrapper isLoading={isLoading}>
            
            {allProblems.length > 0 ? (
                <>
                {showOnlyListOnMobile ? (
                    <ConversationsList 
                        allProblems={allProblems} 
                        handleConversationClick={handleConversationClick}
                        selectedProblem={selectedProblem}
                        getStatusIcon={getStatusIcon}
                        isMobileView={isMobileView}
                        allStates={allStates}
                        allPriorities={allPriorities}
                    />
                ): (
                    <MainContainer style={{height: "85vh"}}>

                        {showListInMainLayout && (
                            <ConversationsList 
                                allProblems={allProblems} 
                                handleConversationClick={handleConversationClick}
                                selectedProblem={selectedProblem}
                                getStatusIcon={getStatusIcon}
                                isMobileView={isMobileView}
                                allStates={allStates}
                                allPriorities={allPriorities}
                            />
                        )}

                        
                        {selectedProblem ? (
                            <ChatContainer>       
                                <ConversationHeader key="1">
                                    {isMobileView && <ConversationHeader.Back key="1" onClick={()=>setSelectedProblem(null)} />}
                                    <ConversationHeader.Content>
                                        <div>
                                            <div className={`flex justify-between items-center gap-2`}>
                                                <div>
                                                    <p className="font-bold">{truncateText(selectedProblem.description, 120)}</p>
                                                    <div className="flex items-center text-sm">
                                                        <i className="pi pi-user me-2"></i>
                                                        <p>{selectedProblem.customerName} #{selectedProblem.pmsBookingNumber}</p>
                                                    </div>
                                                </div>
                                                <Button className="shrink-0" icon="pi pi-ellipsis-h" onClick={()=>setShowDialog(true)} outlined />
                                            </div>
                                            <div className={`flex flex-wrap mt-1 ${isMobileView ? "text-xs" : "text-sm"}`}>
                                                <Chip label={selectedProblem.priority} className={`text-xs text-white ${selectedProblem.priority}-bg my-1`}/>
                                                <Divider layout="vertical" className="mx-2 my-1 p-0" />
                                                <Chip label={selectedProblem.status} className="text-xs my-1" icon={getStatusIcon(selectedProblem.status)} />
                                                {selectedProblem.staffName && (
                                                    <>
                                                        <Divider layout="vertical" className="mx-2 my-1 p-0" />
                                                        <div className="flex items-center my-1">
                                                            <i className={`pi pi-id-card me-2`}></i>
                                                            <p>{selectedProblem.staffName}</p>
                                                        </div>
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                    </ConversationHeader.Content>
                                </ConversationHeader>

                                <MessageList key="2" loading={loadingMessages}>
                                    <MessageList.Content>
                                        <ChatMessages chat={chat} />
                                    </MessageList.Content>
                                </MessageList>

                                {/* Message Input */}
                                {selectedProblem.solution ? (
                                    <MessageInput 
                                        key="3"
                                        disabled
                                        attachButton={false}
                                        sendButton={false}
                                        value={`<div style="text-align: center;"> Marked as <strong>${selectedProblem.status}</strong><br>Solution: ${selectedProblem.solution}</div>`}
                                    />
                                ) : (
                                    <MessageInput 
                                        key="3" 
                                        placeholder="Type message here" 
                                        autoFocus 
                                        attachButton={false} 
                                        value={message}
                                        disabled={!chat}
                                        onChange={(data)=> setMessage(data)}
                                        onSend={sendMessage}
                                    />
                                )}

                            </ChatContainer>
                        ) : (
                            <p className="w-full flex justify-center items-center">Select a Conversation</p>
                        )}
                    </MainContainer>
                ) }
                    
                </>
            ) : (
                <p>No Problems reported</p>
            )}
            <Dialog
                header="Details"
                visible={showDialog}
                style={{ width: isMobileView ? "90vw" : "50vw" }}
                onHide={() => setShowDialog(false)}
            >
                <ProblemDetails 
                    problem={selectedProblem} 
                    allPriorities={allPriorities} 
                    allStates={allStates} 
                    setShowDialog={setShowDialog} 
                    onSave={updateProblemInState}
                />
            </Dialog>
        </NetworkWrapper>
    )
});

function ConversationsList({ allProblems, handleConversationClick, selectedProblem, getStatusIcon, isMobileView, allStates, allPriorities}){

    const [showFiltersPanel, setShowFiltersPanel] = useState(false);
    const [searchText, setSearchText] = useState("");
    const [filters, setFilters] = useState({});

    const isFiltersApplied = Object.values(filters).some(
        value => Array.isArray(value) ? value.length > 0 : Boolean(value)
    );

    let filteredProblems = allProblems;
    // Filter Problems from Search text
    if(searchText){
        const normalizedSearch = searchText.toLowerCase();
        filteredProblems = filteredProblems.filter(problem => {
            const searchableFields = [ problem.pmsBookingNumber, problem.status, problem.customerName, problem.staffName, problem.description, problem.priority, problem.location ];

            return searchableFields.some(field =>
                field?.toString().toLowerCase().includes(normalizedSearch)
            );
        });
    }
    // Filter Problems from Filters applied
    if(isFiltersApplied){
        filteredProblems = allProblems.filter(item =>
            Object.entries(filters).every(([key, filterValue]) => {
                if (!filterValue || filterValue.length === 0) return true;
                return filterValue.includes(item[key]);
            })
        );
    }

    return (
        <div className={`border-2 ${isMobileView ? "" : "w-96"}`}>
            <Sidebar position="left">
                <div >
                    <div className="flex m-2 gap-2 items-center">
                        <div className="grow">
                            <Search 
                                placeholder="Search..." 
                                value={searchText} 
                                onChange={(value) => { setSearchText(value) }}
                                onClearClick={() => { setSearchText("") }}
                            />
                        </div>
                        {/* Filter Button */}
                        <span className="cursor-pointer p-2 text-cyan-600 hover:bg-indigo-50 rounded" onClick={()=> setShowFiltersPanel(!showFiltersPanel)}>
                            <i className="pi pi-filter p-overlay-badge text-lg">
                                {isFiltersApplied && <Badge severity="danger"></Badge>}
                            </i>
                        </span>
                    </div>

                    {showFiltersPanel && (
                        <div className="border-b mb-2">
                            <div className="bg-white p-1 grid grid-cols-2 gap-3">
                                <MultiSelect
                                    value={filters.status}
                                    options={allStates}
                                    placeholder="Status"
                                    className="w-full h-8 text-xs rounded-md"
                                    onChange={e =>
                                        setFilters({ ...filters, status: e.value })
                                    }
                                />

                                <MultiSelect
                                    value={filters.priority}
                                    options={allPriorities}
                                    placeholder="Priority"
                                    className="w-full h-8 text-xs rounded-md"
                                    onChange={e =>
                                        setFilters({ ...filters, priority: e.value })
                                    }
                                />
                            </div>
                            <div className="flex justify-end">
                                <Button label="Clear filters" className="text-right text-sm me-4 focus:ring-0" link onClick={()=>setFilters({})}/>
                            </div>
                        </div>
                    )}

                    {filteredProblems.length === 0 && (
                        <div className="text-center my-8">
                            <p>No results found.</p>
                        </div>
                    )}
                </div>
                
                <ConversationList>
                    {filteredProblems.map(problem => (
                        <Conversation
                            key={problem.id}
                            className={`${problem.priority} border-bottom`}
                            unreadDot={problem.hasUnreadMessages}
                            name={
                                <div className="flex gap-2 justify-between items-center">
                                    <p>{truncateText(problem.description, 50)}</p>
                                </div>
                            }
                            info={
                                <div className="flex flex-col">
                                    <div className="mt-1 flex">
                                        <div className="flex items-center">
                                            <i className="pi pi-user me-1 text-xs"></i>
                                            <p>{problem.customerName} #{problem.pmsBookingNumber}</p>
                                        </div>
                                    </div>
                                    <div className="mt-1 flex">
                                        <div className="flex items-center">
                                            <div className={`w-2 h-2 rounded-full me-1 ${problem.priority}-bg`}/>
                                            <p className="text-xs">{problem.priority}</p>
                                        </div>
                                        <Divider layout="vertical" className="m-0 mx-2 p-0" />
                                        <div className="flex items-center">
                                            <i className={`${getStatusIcon(problem.status)} me-1 text-xs`}></i>
                                            <p className="text-xs">{problem.status}</p>
                                        </div>
                                        {problem.staffName && (
                                            <>
                                                <Divider layout="vertical" className="m-0 mx-2 p-0" />
                                                <div className="flex items-center">
                                                    <i className={`pi pi-id-card me-1`}></i>
                                                    <p className="text-xs">{problem.staffName}</p>
                                                </div>
                                            </>
                                        )}
                                    </div>
                                </div>
                            }
                            onClick={() => handleConversationClick(problem)}
                            active={selectedProblem?.id === problem.id}
                        />
                    ))}

                </ConversationList>
            </Sidebar>
        </div>
    )
}

function ChatMessages ({chat}){

    if(chat?.messages?.length === 0){
        return (
            <div className="mt-20 w-full h-full text-center">
                <p className="mb-4">No Messages</p>
            </div>
        )
    }


    const textedByCrew = (messageData) => {
        let participant = chat.participants.find(participant => participant.name === messageData.senderName);
        return participant?.type === "Crew";
    }

    return (
        <div className="mb-4">
            {chat?.messages.map((messageData, index) => {
                const isCurrentMessageFromCrew = textedByCrew(messageData);

                const isPreviousMessageFromCrew = index > 0 ? textedByCrew(chat.messages[index - 1]) : null;
                const showSenderName = isPreviousMessageFromCrew !== isCurrentMessageFromCrew;
                
                return (
                    <Message 
                        key={`${messageData.chat_id}#${index}`} 
                        model={{
                            sender: messageData.senderName,
                            message: messageData.message,
                            direction: isCurrentMessageFromCrew ? "outgoing" : "incoming",
                            position: "normal"
                        }}
                    >
                        <Message.Header>
                            {showSenderName && (
                                <div className={`${isCurrentMessageFromCrew ? "ms-auto" : ""}`}>
                                    {messageData.senderName}
                                </div>
                            )}
                        </Message.Header>
                    </Message>
                )
            })}
        </div>
    )
}

function ProblemDetails({ problem, allPriorities, allStates, setShowDialog, onSave }) {
    const [formData, setFormData] = useState(problem);
    const [errorFields, setErrorFields] = useState([]);
    const [isModified, setIsModified] = useState(false);

    const {toast} = useNotificationContext();
  
    const updateField = (key, value) => {
        setFormData(prev => ({ ...prev, [key]: value }));
        setIsModified(true);
    };
  
    const showSolution = useMemo(()=> (
        formData.status === "Resolved" || formData.status === "Unresolved"
    ),[formData]);
  
    const payload = useMemo(() => ({
        id: formData.id,
        customerName: formData.customerName,
        location: formData.location,
        pmsUniqueId: formData.pmsUniqueId,
        pmsBookingNumber: formData.pmsBookingNumber,
        problemCategory: {
            id: formData.problemCategory?.id
        },
        description: formData.description,
        publish: formData.publish,
        customerCallRequired: formData.customerCallRequired,
        status: formData.status,
        solution: showSolution ? formData.solution || "" : "",
        staffName: formData.staffName,
        priority: formData.priority
    }), [formData, showSolution]);

    const isValid = () => {
        const errors = [];
        // Resolved and Unresolved state must need a solution
        if((formData.status === "Resolved" || formData.status === "Unresolved") && (!formData.solution)){
            errors.push("solution");
        }
        setErrorFields(errors);

        return errors.length === 0;
    }
  
    const handleSave = () => {
        if(isValid()){
            // Save it to Hub
            putToHub(
                "problem/v2/admin/problems",
                payload,
                (data) => { // success callback
                    onSave(data);
                    toast.current.show({severity: 'success', summary: 'Problem Updated'});
                    setIsModified(false);
                    setShowDialog(false);
                },
                () => { // failure callback
                    toast.current.show({severity: 'error', summary: 'Oops! Something went wrong'});
                }
            )
        }
    }
  
    const ReadOnlyItem = ({ label, value }) => (
        <div style={{ width: "30%" }} className="mb-3">
            <div className="text-500 text-sm">{label}</div>
            <div className="font-medium">{value || "-"}</div>
        </div>
    );
  
    return (
        <div className="p-3">
    
            {/* READ ONLY */}
            <div className="flex flex-wrap gap-6">
                <ReadOnlyItem label="Customer Name" value={formData.customerName} />
                <ReadOnlyItem label="Creation Date" value={formData.creationDateTime} />
                <ReadOnlyItem label="Last Updated" value={formData.lastUpdateDateTime} />
                <ReadOnlyItem label="PMS Booking Nr" value={formData.pmsBookingNumber} />
                <ReadOnlyItem label="Department" value={formData.department?.name?.defaultValue} />
                <ReadOnlyItem label="Location" value={formData.location} />
                <ReadOnlyItem label="Problem Category" value={formData.problemCategory?.name?.defaultValue} />
                <ReadOnlyItem label="Problem Number" value={formData.problemNumber} />
            </div>
    
            <Divider />
    
            <div className="mb-4">
                <div className="text-500 text-sm mb-1">Problem Description</div>
                <div className="surface-100 font-medium">
                    {formData.description || "-"}
                </div>
            </div>
    
            <Divider />
    
            {/* EDITABLE */}
            <div className="mb-4 w-3/6">
                <label className="font-medium">Staff Name</label>
                <InputText 
                    value={formData.staffName} 
                    className="w-full mt-2"
                    onChange={(e) => updateField("staffName", e.target.value)} 
                />
            </div>

            <div className="flex flex-wrap gap-5 align-items-center mb-4">
                <label className="font-medium">Customer Call Required</label>
                <InputSwitch
                    checked={formData.customerCallRequired}
                    onChange={e => updateField("customerCallRequired", e.value)}
                />
            </div>

            <div className="flex align-items-center gap-3 mb-4">
                <label className="font-medium">Publish</label>
                <InputSwitch
                    checked={formData.publish}
                    onChange={e => updateField("publish", e.value)}
                /> 
            </div>
    
            <div className="flex flex-wrap gap-4 mb-4">
                <div className="flex-grow">
                    <label className="font-medium">Priority</label>
                    <Dropdown
                        value={formData.priority}
                        options={allPriorities}
                        onChange={e => updateField("priority", e.value)}
                        className="w-full mt-2"
                    />
                </div>
        
                <div className="flex-grow">
                    <label className="font-medium">Status</label>
                    <Dropdown
                        value={formData.status}
                        options={allStates}
                        onChange={e => updateField("status", e.value)}
                        className="w-full mt-2"
                    />
                </div>
            </div>
    
            {showSolution && (
                <div className="mb-4">
                    <label className="font-medium">Solution</label>
                    <InputTextarea
                        value={formData.solution}
                        rows={4}
                        onChange={e => updateField("solution", e.target.value)}
                        className="w-full mt-2"
                        invalid={errorFields.includes("solution")}
                    />
                </div>
            )}
    
            {/* ACTION */}
            <div className="flex justify-content-end">
                <Button
                    label="Save"
                    icon="pi pi-save"
                    disabled={!isModified}
                    onClick={handleSave}
                />
            </div>
        </div>
    );
  }